import { TextField, ListItem, FormControl } from '@material-ui/core';
import List from '@material-ui/core/List';
import ConfirmModal from 'Components/Common/Modals/ConfirmModal';
import useAppSelector from 'Hooks/Redux/useAppSelector';
import useMessagesDispatch from 'Hooks/Redux/useMessagesDispatch';
import { UserResponse } from 'Models/User';
import { ChangeEvent, ReactElement, useState } from 'react';
import { MessageStatusLevel } from 'Redux/Actions/Messages';
import patchUserPassword from 'Utils/Http/Requests/Users/PatchUserPassword';

interface ChangeUserPasswordModalProps {
	isOpen: boolean;
	onClose: () => void;
	user: UserResponse | null;
}

const ChangeUserPasswordModal = ({ isOpen, onClose, user }: ChangeUserPasswordModalProps): ReactElement => {
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [passwordError, setPasswordError] = useState('');
	const [confirmPasswordError, setConfirmPasswordError] = useState('');

	const token = useAppSelector((x) => x.profile.token);
	const { dispatchSetAxiosErrorMessage, dispatchSetMessageAction } = useMessagesDispatch();

	const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
		setPassword(event.target.value);
	};

	const handleConfirmPasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
		setConfirmPassword(event.target.value);
	};

	const validatePassword = (): void => {
		if (password.length < 8) {
			setPasswordError('Password must be at least 8 characters long');
		} else {
			setPasswordError('');
		}
	};

	const validateConfirmPassword = (): void => {
		if (password !== confirmPassword) {
			setConfirmPasswordError('Passwords do not match');
		} else {
			setConfirmPasswordError('');
		}
	};

	const handleConfirm = async (): Promise<void> => {
		if (!user) return;

		try {
			await patchUserPassword(token, user.id, password);
			dispatchSetMessageAction(`Password changed successfully for user ${user.email}`, MessageStatusLevel.Success);
			onClose();
		} catch (e) {
			dispatchSetAxiosErrorMessage(e);
		}
	};

	return (
		<ConfirmModal
			open={isOpen}
			onClose={onClose}
			onConfirmClicked={handleConfirm}
			title="Change Password"
			confirmButtonText="Change"
			confirmButtonDisabled={!!passwordError || !!confirmPasswordError || !password || !confirmPassword}
		>
			<List>
				<ListItem>
					<FormControl fullWidth>
						<TextField
							fullWidth
							type="password"
							label="New Password"
							value={password}
							onChange={handlePasswordChange}
							onBlur={validatePassword}
							onFocus={() => setPasswordError('')}
							error={!!passwordError}
							helperText={passwordError}
							margin="normal"
						/>
					</FormControl>
				</ListItem>
				<ListItem>
					<FormControl fullWidth>
						<TextField
							fullWidth
							type="password"
							label="Confirm Password"
							value={confirmPassword}
							onChange={handleConfirmPasswordChange}
							onBlur={validateConfirmPassword}
							onFocus={() => setConfirmPasswordError('')}
							error={!!confirmPasswordError}
							helperText={confirmPasswordError}
							margin="normal"
						/>
					</FormControl>
				</ListItem>
			</List>
		</ConfirmModal>
	);
};

export default ChangeUserPasswordModal;
